import React, { useCallback, useState, useEffect } from 'react';

// Import react-grid-layout for web
import GridLayout from 'react-grid-layout';
import { Responsive, WidthProvider } from 'react-grid-layout';

// Import CSS
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Create responsive grid layout component
const ResponsiveGridLayout = WidthProvider(Responsive);
const SimpleGridLayout = WidthProvider(GridLayout);

type LayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
};

interface GridLayoutWrapperProps {
  children: React.ReactNode[];
  layouts?: { [key: string]: LayoutItem[] };
  breakpoints?: { lg: number; md: number; sm: number; xs: number };
  cols?: { lg: number; md: number; sm: number; xs: number };
  rowHeight?: number;
  margin?: [number, number];
  containerPadding?: [number, number];
  onLayoutChange?: (layout: LayoutItem[], layouts?: { [key: string]: LayoutItem[] }) => void;
  isDraggable?: boolean;
  isResizable?: boolean;
  compactType?: 'vertical' | 'horizontal' | null;
  preventCollision?: boolean;
  responsive?: boolean;
  editMode?: boolean;
}

export function GridLayoutWrapper({
  children,
  layouts = {},
  breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480 },
  cols = { lg: 12, md: 8, sm: 4, xs: 2 },
  rowHeight = 30,
  margin = [10, 10],
  containerPadding = [10, 10],
  onLayoutChange,
  isDraggable = true,
  isResizable = true,
  compactType = 'vertical',
  preventCollision = false,
  responsive = true,
  editMode = false
}: GridLayoutWrapperProps) {
  const [mounted, setMounted] = useState(false);
  const [width, setWidth] = useState(1200);

  useEffect(() => {
    setMounted(true);
    const updateWidth = () => {
      const container = document.querySelector('.grid-layout-container');
      if (container) {
        setWidth(container.clientWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleLayoutChange = useCallback((layout: LayoutItem[], allLayouts?: { [key: string]: LayoutItem[] }) => {
    if (onLayoutChange) {
      onLayoutChange(layout, allLayouts);
    }
  }, [onLayoutChange]);

  // Don't render on server
  if (!mounted) {
    return null;
  }

  const gridProps = {
    breakpoints,
    cols,
    rowHeight,
    margin,
    containerPadding,
    onLayoutChange: handleLayoutChange,
    isDraggable: isDraggable && editMode,
    isResizable: isResizable && editMode,
    compactType,
    preventCollision,
    draggableHandle: editMode ? '.grid-drag-handle' : '',
    resizeHandles: editMode ? ['se'] : [],
    className: 'grid-layout-container'
  };

  // Wrap children with keys and data-grid attributes
  const wrappedChildren = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return null;
    
    const key = child.key || `grid-item-${index}`;
    const gridData = child.props['data-grid'] || {
      x: 0,
      y: 0,
      w: 3,
      h: 2,
      minW: 1,
      minH: 1
    };

    // Don't double-wrap if already wrapped
    if (child.type === 'div' && child.props['data-grid']) {
      return child;
    }

    return (
      <div 
        key={key} 
        data-grid={gridData}
        style={{ width: '100%', height: '100%' }}
      >
        {child}
      </div>
    );
  });

  if (responsive && ResponsiveGridLayout) {
    return (
      <ResponsiveGridLayout
        {...gridProps}
        layouts={layouts}
      >
        {wrappedChildren}
      </ResponsiveGridLayout>
    );
  }

  // Default layout for non-responsive mode
  const defaultLayout = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return null;
    
    const key = child.key || `grid-item-${index}`;
    const gridData = child.props['data-grid'] || {
      i: key,
      x: 0,
      y: 0,
      w: 3,
      h: 2,
      minW: 1,
      minH: 1
    };

    return {
      i: key,
      ...gridData
    };
  }).filter(Boolean) as LayoutItem[];

  return SimpleGridLayout ? (
    <SimpleGridLayout
      {...gridProps}
      layout={defaultLayout}
      cols={cols.lg}
    >
      {wrappedChildren}
    </SimpleGridLayout>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
      {wrappedChildren}
    </div>
  );
}